import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import { gsap } from 'gsap';

import Env from 'lib/Env';
import { getUsageDevButtonsEnabled } from 'lib/browserStorage';
import { getClampedPercentage } from 'lib/utilityFunctions';
import { localizedStrings } from 'lib/l10n/l10n';
import useFocusTrap from 'lib/hooks/useFocusTrap';
import { getSelectedProductUsage, getWeeklyUsageTarget } from 'a/selectors/usageSelectors';
import { getHasUserInteracted } from 'a/selectors/appSessionSelectors';
import BtnRaised from 'a/components/Buttons/BtnRaised';
import SpeechBubbleSvg from 'a/assets/svgs/speechBubble.svg';

import { saveUsageData } from '../../actions/questsActions';
import { getCuriosoSkin } from '../../selectors';
import * as CuriosoSequences from '../../sequences/CuriosoSequences';
import CuriosoComponent from '../Curioso/Curioso';
import ConfettiBurst from './ConfettiBurst';
import BackgroundSvg from './assets/background.svg';
import ConfettiSvg from './assets/confetti.svg';
import DevButtons from './DevButtons';

const SPEED_MS_PER_PERCENT = 25;
const MIN_ANIMATION_DURATION_MS = 700;
const LEAP_PX = 80;
const CHAR_WIDTH = 220;
const WALK_THRESHOLD_PERCENT = 10;
const ANIMATION_START_DELAY_MS = 500;

const BookertonWeeklyUsageModal = ({ className, actionButton, onModalClose }) => {
  const dispatch = useDispatch();

  const usageTarget = useSelector(getWeeklyUsageTarget);
  const hasUserInteracted = useSelector(getHasUserInteracted);
  const curiosoSkin = useSelector(getCuriosoSkin);
  const { weeklyUsageTime, lastShownUsageTime, celebrated: targetReached } = useSelector(getSelectedProductUsage);

  const [[startValue, endValue], setInitialValues] = useState([lastShownUsageTime || 0, weeklyUsageTime || 0]);
  const [celebrated, setCelebrated] = useState(!!targetReached);

  const hasTarget = !!usageTarget;
  const startPercent = getClampedPercentage(startValue, usageTarget);
  const devButtonsEnabled = Env.isDevEnv && getUsageDevButtonsEnabled();

  const counterRef = useRef({ value: startValue });
  const barRef = useRef(null);
  const characterRef = useRef(null);
  const curiosoRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const tlRef = useRef(null);

  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState(startValue);
  const [showConfetti, setShowConfetti] = useState(false);

  const playCuriosoAnimation = async (type = 'idle') => {
    if (!curiosoRef.current) {
      return Promise.resolve();
    }

    return curiosoRef.current.playCuriosoAnimation(type);
  };

  const startCuriosoWalkingAnimation = () => {
    if (!curiosoRef.current) {
      return Promise.resolve();
    }
    return curiosoRef.current.startWalking();
  };

  const playCuriosoSound = async (sound = 'happy') => {
    if (hasUserInteracted) {
      return CuriosoSequences.playCuriosoSound(sound);
    }
    return Promise.resolve();
  };

  const animateDisplayValue = (start, end, duration) => {
    gsap.killTweensOf(counterRef.current);
    counterRef.current.value = start;
    gsap.to(counterRef.current, {
      value: end,
      duration: duration / 1000,
      ease: 'none',
      onUpdate: () => setDisplayValue(Math.round(counterRef.current.value)),
    });
  };

  const handleTimelineCompletion = () => {
    // Without a target the bar showed no progress, so the watermark stays where it was: once the target
    // is available again the student sees this week's progress animate - and celebrate - as usual.
    if (!devButtonsEnabled && hasTarget) {
      dispatch(saveUsageData({ lastShownUsageTime: endValue }));
    }

    setBubbleVisible(true);
    playCuriosoAnimation('idle');
    isAnimatingRef.current = false;
  };

  const animateJump = async () => {
    await Promise.allSettled([
      playCuriosoAnimation('jump'),
      tlRef.current
        .to(characterRef.current, { x: LEAP_PX * 0.5, y: -80, duration: 0.5, ease: 'power2.out' })
        .to(characterRef.current, { x: LEAP_PX, y: 0, duration: 0.5, ease: 'power2.in' }),
    ]);
  };

  const animateCurious = (onComplete) => {
    tlRef.current.call(async () => {
      playCuriosoSound('curious');
      setBubbleVisible(true);
      await playCuriosoAnimation('curious');
      onComplete?.();
    });
  };

  const animateWalk = (toPercent, duration, onComplete) => {
    tlRef.current
      .call(() => startCuriosoWalkingAnimation(), null, '<')
      .to(characterRef.current, { left: `${toPercent}%`, duration: duration / 1000, ease: 'none' }, '<')
      .call(() => {
        onComplete?.();
      });
  };

  const celebrate = async () => {
    setCelebrated(true);
    setShowConfetti(true);
    await animateJump();
    playCuriosoSound('affirmative');
    setBubbleVisible(true);
    await playCuriosoAnimation('happy');
  };

  const animateProgress = () => {
    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    tlRef.current?.kill();
    tlRef.current = gsap.timeline();

    if (!hasTarget) {
      animateCurious(handleTimelineCompletion);
      return;
    }

    const shouldCelebrate = endValue >= usageTarget && !celebrated;
    const endPercent = getClampedPercentage(endValue, usageTarget);
    const progressDelta = endPercent - startPercent;
    const duration = Math.max(MIN_ANIMATION_DURATION_MS, Math.round(progressDelta * SPEED_MS_PER_PERCENT));
    const barDuration = Math.max(0, duration - 300);

    // Phase 1: counter tracks bar fill, capped at target
    if (progressDelta > 0) {
      animateDisplayValue(Math.min(startValue, usageTarget), Math.min(endValue, usageTarget), barDuration);
    }

    tlRef.current.to(barRef.current, {
      width: `${endPercent}%`,
      duration: barDuration / 1000,
      ease: 'none',
      onComplete: () => {
        // Phase 2: after bar fills, animate counter beyond target if needed
        if (endValue > usageTarget) {
          const phase2Start = progressDelta > 0 ? usageTarget : startValue;
          const phase2Duration = progressDelta > 0 ? 300 : MIN_ANIMATION_DURATION_MS;
          animateDisplayValue(phase2Start, endValue, phase2Duration);
        }
      },
    });

    if ((progressDelta <= WALK_THRESHOLD_PERCENT && !shouldCelebrate) || celebrated) {
      animateCurious(handleTimelineCompletion);
      return;
    }

    animateWalk(endPercent, duration, async () => {
      if (shouldCelebrate) {
        await celebrate();
      }
      handleTimelineCompletion();
    });
  };

  const mockUsageValues = (newValues) => {
    if (isAnimatingRef.current || !newValues?.length) {
      return;
    }

    const [start] = newValues;

    tlRef.current?.kill();
    gsap.killTweensOf(counterRef.current);
    gsap.set(barRef.current, { clearProps: 'width' });
    gsap.set(characterRef.current, { clearProps: 'left,x,y' });

    setShowConfetti(false);
    setBubbleVisible(false);
    setInitialValues(newValues);
    setCelebrated(hasTarget && start >= usageTarget);
    setDisplayValue(start);
  };

  const handleModalClose = () => {
    onModalClose();
  };

  const curiosoMessageSettings = useMemo(() => {
    const { start, progress, finish, currentUsage } = localizedStrings.usageProgressMessages;

    if (endValue === 0) {
      return { message: start, position: 'right', type: 'shining' };
    }

    if (!hasTarget) {
      return { message: localizedStrings.formatString(currentUsage, endValue), position: 'right', type: 'regular' };
    }

    const position = getClampedPercentage(endValue, usageTarget) >= 82 ? 'left' : 'right';

    return endValue >= usageTarget
      ? { message: finish, position, type: 'shining' }
      : { message: progress, position, type: 'regular' };
  }, [endValue, hasTarget, usageTarget]);

  useEffect(() => {
    const counter = counterRef.current;
    const trigger = document.activeElement;
    const timeoutId = setTimeout(animateProgress, ANIMATION_START_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);

      tlRef.current?.kill();
      gsap.killTweensOf(counter);

      if (trigger && trigger.type === 'button') {
        trigger.focus();
      }
    };
  }, []);

  const dialogRef = useFocusTrap();

  const { icon = null, label = localizedStrings.close, style = {} } = actionButton;

  return (
    <Backdrop className={className} data-dd-action-name="weekly-usage-modal" onClick={handleModalClose}>
      <ConfettiBurst active={showConfetti} />
      <Modal
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="usage-card-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="sr-only" id="usage-card-title">
          {localizedStrings.usageProgress}
        </h2>

        <BarSection>
          <CharacterTrack $skin={curiosoSkin} $left={hasTarget && startValue >= usageTarget ? '50px' : '-50px'}>
            <CharacterWrapper ref={characterRef} $leftPercent={startPercent}>
              <div aria-live="polite" aria-atomic="true">
                {bubbleVisible && (
                  <SpeechBubble
                    $position={curiosoMessageSettings.position}
                    $type={curiosoMessageSettings.type}
                  >
                    <IconWrapper>
                      <SpeechBubbleSvg />
                    </IconWrapper>
                    <span>{curiosoMessageSettings.message}</span>
                    {hasTarget && (
                      <span className="sr-only">
                        {localizedStrings.formatString('usageProgressDetail', endValue, usageTarget)}
                      </span>
                    )}
                  </SpeechBubble>
                )}
              </div>

              <CuriosoComponent ref={curiosoRef} width={CHAR_WIDTH} height={CHAR_WIDTH} isCuriosoTurnedRight />
            </CharacterWrapper>
          </CharacterTrack>

          {hasTarget ? (
            <ProgressTrack
              role="progressbar"
              aria-label={localizedStrings.usageProgress}
              aria-valuenow={displayValue}
              aria-valuemin={0}
              aria-valuemax={usageTarget}
              aria-valuetext={localizedStrings.formatString('usageProgressDetail', displayValue, usageTarget)}
            >
              <ProgressFill ref={barRef} $widthPercent={startPercent} $showGradient={displayValue > usageTarget}>
                {displayValue < usageTarget && <ProgressFromLabel>{displayValue}</ProgressFromLabel>}
              </ProgressFill>
              <ConfettiHorn
                src={ConfettiSvg}
                alt=""
                aria-hidden="true"
                $celebrate={showConfetti}
                $hidden={celebrated && !showConfetti}
              />
              <ProgressToLabel>{displayValue >= usageTarget ? displayValue : usageTarget} min</ProgressToLabel>
            </ProgressTrack>
          ) : (
            <ProgressTrack />
          )}
        </BarSection>

        <ActionButton
          type="button"
          style={style}
          data-testid="usage-action-button"
          data-dd-action-name="weekly-usage-modal:continue"
          size="medium"
          icon={icon}
          onClick={handleModalClose}
        >
          {label}
        </ActionButton>

        {devButtonsEnabled && (
          <StyledDevButtons
            startValue={startValue}
            endValue={endValue}
            usageTarget={usageTarget}
            onSetButtonClick={mockUsageValues}
            onRunButtonClick={animateProgress}
          />
        )}
      </Modal>
    </Backdrop>
  );
};

BookertonWeeklyUsageModal.propTypes = {
  className: PropTypes.string,
  actionButton: PropTypes.shape({
    icon: PropTypes.string,
    label: PropTypes.string,
    style: PropTypes.object,
  }),
  onModalClose: PropTypes.func,
};

BookertonWeeklyUsageModal.defaultProps = {
  className: '',
  actionButton: {},
  onModalClose: () => {},
};

export default BookertonWeeklyUsageModal;

const bounce = keyframes`
  0%   { transform: translateY(-100%) scale(1) rotate(0deg); }
  25%  { transform: translateY(-140%) scale(1.3) rotate(-15deg); }
  50%  { transform: translateY(-100%) scale(1) rotate(0deg); }
  75%  { transform: translateY(-130%) scale(1.2) rotate(12deg); }
  100% { transform: translateY(-100%) scale(1) rotate(0deg); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const MESSAGE_POSITIONS = {
  left: css`
    left: 55px;
    transform: translate(-100%, 0);
    div {
      transform: translate(-50%, -50%) scaleX(-1);
    }
  `,
  right: css`
    right: 35px;
    transform: translate(100%, 0);
  `,
};

const CURIOSO_BOTTOM_OFFSETS = {
  scaly: -10,
  feathery: -25,
  furry: -25,
};

const showParticles = css`
  [class^='particle'] {
    animation: ${fadeIn} 0.6s ease-in forwards;
  }

  [class^='particle-circle']:nth-of-type(odd) {
    animation-delay: 0.3s;
  }
  [class^='particle-star']:nth-of-type(even) {
    animation-delay: 0.6s;
  }
  [class^='particle-circle']:nth-of-type(even) {
    animation-delay: 0.9s;
  }
  [class^='particle-star']:nth-of-type(odd) {
    animation-delay: 1.2s;
  }
`;

const Backdrop = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: rgba(56, 78, 74, 0.8);
`;

const Modal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 783px;
  height: 464px;
  padding: 55px 50px 75px;
  position: absolute;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  background-image: url(${BackgroundSvg});
  background-position: center;
  background-color: transparent;
  background-repeat: no-repeat;
  background-size: contain;
`;

const ConfettiHorn = styled.img`
  width: 90px;
  height: 90px;
  position: absolute;
  transform: translateY(-100%);
  right: -15px;
  top: 0;
  animation: ${({ $celebrate }) =>
    ($celebrate
      ? css`
          ${bounce} 0.6s ease-in-out 2, ${fadeOut} 0.3s ease-in 0.5s forwards
        `
      : 'none')};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

const BarSection = styled.div`
  width: 100%;
  position: relative;
  margin-top: auto;
`;

const CharacterTrack = styled.div`
  width: calc(100% + 50px - ${CHAR_WIDTH * 1.3}px);
  position: relative;
  left: ${({ $left }) => $left || '-50px'};
  bottom: ${({ $skin }) => CURIOSO_BOTTOM_OFFSETS[$skin] || 0}px;
`;

const CharacterWrapper = styled.div`
  pointer-events: none;
  width: ${CHAR_WIDTH}px;
  height: ${CHAR_WIDTH}px;
  position: absolute;
  bottom: 0;
  left: ${({ $leftPercent }) => $leftPercent}%;
`;

const SpeechBubble = styled.div`
  width: 230px;
  height: 148px;
  padding: 21px 27px 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -60px;
  ${({ $position }) => MESSAGE_POSITIONS[$position]};
  font-size: 19px;
  line-height: 25px;
  font-family: inherit;
  color: #464646;
  text-align: center;
  white-space: pre-line;
  opacity: 0;
  animation: ${fadeIn} 0.4s 0.2s ease-in-out forwards;
  span {
    display: inline-block;
  }
  [class^='particle'] {
    opacity: 0;
  }
  ${({ $type }) => $type === 'shining' && showParticles};
`;

const IconWrapper = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  top: 55%;
  left: 50%;
  z-index: -1;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 42px;
  display: flex;
  border-radius: 21px;
  background: #fbf6e6;
  position: relative;
  font-family: Quicksand Bold;
  color: #2b1d50;
  font-size: 21px;
  line-height: 35px;
  white-space: nowrap;
`;

const ProgressFill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  width: ${({ $widthPercent }) => $widthPercent}%;
  padding: 0 10px;
  height: 100%;
  background: ${({ $showGradient }) => ($showGradient ? 'linear-gradient(to right, #FFE742 55%, #FFA530)' : '#FFE742')};
  border-radius: 22px;
  background-clip: padding-box;
  border: 6px solid transparent;
`;

const ProgressFromLabel = styled.span``;

const ProgressToLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-50%);
  position: absolute;
  right: 15px;
  top: 50%;
`;

const ActionButton = styled(BtnRaised)`
  touch-action: manipulation;
  user-select: none;
  min-width: 240px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  font-size: 33px;
  --btn-height: 70px;
  background: #3f388f;
  box-shadow: inset 0px -6px 0px #292368;
  svg path {
    fill: #fff;
  }
  &:active {
    background: #292368;
  }
`;

const StyledDevButtons = styled(DevButtons)`
  width: 80%;
`;
